import * as Sentry from '@sentry/react';
import { put } from 'redux-saga/effects';
import {
  getAccess,
  grantAccess,
  removeAccess,
} from 'api/cloudsave/cloudsaveApi';
import {
  errorMessage,
  storageUnitAccessGranted,
  storageUnitAccessRemoved,
} from 'containers/Snackbar/snackbarMessages';
import { isPendingCreation } from 'containers/Dashboard/utils/isPendingCreation';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import {
  clearMembers,
  getAccessInformationForStorageUnitFromContextMenuSuccess,
  getAccessInformationForStorageUnitFromMembersSuccess,
  grantAccessFromContextMenuSuccess,
  grantAccessFromMembersSuccess,
  removeAccessFromContextMenuSuccess,
  removeAccessFromMembersSuccess,
  setDirectAccess,
  setMembersLoading,
  setSharedWithUsersLoading,
} from './reducer';
import type {
  GetAccessInformationForStorageUnitRequestActionInterface,
  GrantAccessToStorageUnitRequestActionInterface,
  RemoveAccessToStorageUnitRequestActionInterface,
} from './sagaActions';

export function* grantAccessToStorageUnit(
  action: GrantAccessToStorageUnitRequestActionInterface,
) {
  const { storageUnitId, access, openedThroughMembers } = action.payload;
  try {
    if (openedThroughMembers) {
      yield put(setMembersLoading(true));
    }
    yield put(setSharedWithUsersLoading(true));
    const { parsedBody } = yield withRetry(() =>
      grantAccess(storageUnitId, access),
    );
    const { succeeded, failed } = parsedBody;
    if (openedThroughMembers) {
      yield put(grantAccessFromMembersSuccess(succeeded));
    } else {
      yield put(
        grantAccessFromContextMenuSuccess({ users: succeeded, storageUnitId }),
      );
    }

    if (failed.length > 0) {
      addMessage(errorMessage('Could not share with ' + failed.join(', ')));
    } else {
      yield put(addMessage(storageUnitAccessGranted()));
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'grantAccessToStorageUnit' },
      extra: { storageUnitId, emailCount: access.emails.length },
    });
    yield put(
      addMessage(
        errorMessage('Could not share with any of the provided users'),
      ),
    );
  } finally {
    if (openedThroughMembers) {
      yield put(setMembersLoading(false));
    }
    yield put(setSharedWithUsersLoading(false));
  }
}

export function* getAccessInformationForStorageUnit(
  action: GetAccessInformationForStorageUnitRequestActionInterface,
) {
  const { storageUnitId, openedThroughMembers } = action.payload;
  if (isPendingCreation(storageUnitId)) {
    if (openedThroughMembers) {
      yield put(clearMembers());
      yield put(setDirectAccess(true));
    }
    return;
  }
  try {
    if (openedThroughMembers) {
      yield put(setMembersLoading(true));
    } else {
      yield put(setSharedWithUsersLoading(true));
    }
    const { parsedBody } = yield withRetry(() => getAccess(storageUnitId));
    if (openedThroughMembers) {
      yield put(
        getAccessInformationForStorageUnitFromMembersSuccess(parsedBody),
      );
      yield put(setDirectAccess(true));
    } else {
      yield put(
        getAccessInformationForStorageUnitFromContextMenuSuccess(parsedBody),
      );
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'getAccessInformationForStorageUnit' },
      extra: { storageUnitId },
      level: 'warning',
    });
    yield put(setDirectAccess(false));
  } finally {
    if (openedThroughMembers) {
      yield put(setMembersLoading(false));
    } else {
      yield put(setSharedWithUsersLoading(false));
    }
  }
}

export function* removeAccessToStorageUnit(
  action: RemoveAccessToStorageUnitRequestActionInterface,
) {
  const { id, access, openedThroughMembers } = action.payload;
  try {
    if (openedThroughMembers) yield put(setMembersLoading(true));
    yield put(setSharedWithUsersLoading(true));
    yield withRetry(() => removeAccess(id, access));
    if (openedThroughMembers) {
      yield put(removeAccessFromMembersSuccess(access.email));
    } else {
      yield put(
        removeAccessFromContextMenuSuccess({
          email: access.email,
          userId: access.id,
          storageUnitId: id,
        }),
      );
    }
    yield put(addMessage(storageUnitAccessRemoved()));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'removeAccessToStorageUnit' },
      extra: { storageUnitId: id, userEmail: access.email },
    });
    yield put(addMessage(errorMessage('Could not remove access')));
  } finally {
    if (openedThroughMembers) yield put(setMembersLoading(false));
    yield put(setSharedWithUsersLoading(false));
  }
}
